import React from 'react';
import { Box, Flex, Grid, GridItem, HStack, Separator, Stack, Table, VStack } from '@chakra-ui/react';
import { Skeleton } from '@/components/ui/skeleton';

const FieldSkeleton = ({ helper = false }: { helper?: boolean }) => (
  <Stack gap={2}>
    <Skeleton height='16px' width='128px' />
    <Skeleton height='40px' width='full' borderRadius='md' />
    {helper && <Skeleton height='14px' width='70%' />}
  </Stack>
);

const SettingsSectionSkeleton = ({ fields, wide = false }: { fields: number; wide?: boolean }) => (
  <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={10} my='8'>
    <GridItem rowSpan={{ base: 1, lg: 2 }} colSpan={{ base: 1, md: 2, lg: 1 }}>
      <Stack gap={2}>
        <Skeleton height='18px' width='168px' />
        <Skeleton height='14px' width='220px' />
      </Stack>
    </GridItem>
    {Array.from({ length: fields }).map((_, index) => (
      <GridItem key={index} colSpan={wide ? { base: 1, md: 2, lg: 2 } : 1}>
        <FieldSkeleton helper={wide} />
      </GridItem>
    ))}
  </Grid>
);

const AddressSectionSkeleton = () => (
  <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={10} my='8'>
    <GridItem colSpan={{ base: 1, md: 2, lg: 1 }}>
      <Stack gap={2}>
        <Skeleton height='18px' width='184px' />
        <Skeleton height='14px' width='220px' />
      </Stack>
    </GridItem>
    <GridItem colSpan={{ base: 1, md: 2, lg: 2 }}>
      <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }} gap={10}>
        {[0, 1, 2, 3].map((item) => (
          <GridItem key={item}>
            <FieldSkeleton />
          </GridItem>
        ))}
      </Grid>
    </GridItem>
  </Grid>
);

export const OrganisationSettingsSkeleton = () => (
  <Box aria-busy='true'>
    <SettingsSectionSkeleton fields={4} />
    <Separator />
    <AddressSectionSkeleton />
    <Separator />
    <SettingsSectionSkeleton fields={1} wide />
    <Separator />
    <SettingsSectionSkeleton fields={1} wide />
    <Separator />
    <SettingsSectionSkeleton fields={1} wide />
    <Flex justifyContent='flex-end'>
      <HStack gap={4}>
        <Skeleton height='40px' width='150px' borderRadius='md' />
      </HStack>
    </Flex>
  </Box>
);

export const AccessUsersSkeleton = () => (
  <VStack gap={6} alignItems='start' aria-busy='true'>
    <Box
      width='100%'
      display={{ base: 'block', md: 'flex' }}
      justifyContent='space-between'
      alignItems='center'
      gap={4}
    >
      <HStack gap={2} width={{ base: '100%', md: 'auto' }} minW={{ md: '390px' }}>
        <Skeleton height='40px' flex='1' borderRadius='md' />
        <Skeleton height='40px' width='86px' borderRadius='md' />
      </HStack>
      <Skeleton height='40px' width='120px' borderRadius='md' mt={{ base: 4, md: 0 }} />
    </Box>
    <Table.Root>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader>
            <Skeleton height='16px' width='88px' />
          </Table.ColumnHeader>
          <Table.ColumnHeader hideBelow='md'>
            <Skeleton height='16px' width='84px' />
          </Table.ColumnHeader>
          <Table.ColumnHeader hideBelow='md'>
            <Skeleton height='16px' width='56px' />
          </Table.ColumnHeader>
          <Table.ColumnHeader hideBelow='md'>
            <Skeleton height='16px' width='64px' />
          </Table.ColumnHeader>
          <Table.ColumnHeader width='50px' />
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {[0, 1, 2].map((row) => (
          <Table.Row key={row}>
            <Table.Cell>
              <Stack gap={2}>
                <Skeleton height='16px' width='160px' />
                <Skeleton height='14px' width='220px' />
              </Stack>
            </Table.Cell>
            <Table.Cell hideBelow='md'>
              <Skeleton height='16px' width='96px' />
            </Table.Cell>
            <Table.Cell hideBelow='md'>
              <Skeleton height='24px' width='112px' borderRadius='full' />
            </Table.Cell>
            <Table.Cell hideBelow='md'>
              <Skeleton height='24px' width='72px' borderRadius='full' />
            </Table.Cell>
            <Table.Cell>
              <Skeleton height='32px' width='32px' borderRadius='md' />
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  </VStack>
);

export const AuthTokenListSkeleton = () => (
  <Table.Root aria-busy='true'>
    <Table.Header>
      <Table.Row>
        <Table.ColumnHeader>
          <Skeleton height='16px' width='96px' />
        </Table.ColumnHeader>
        <Table.ColumnHeader>
          <Skeleton height='16px' width='84px' />
        </Table.ColumnHeader>
        <Table.ColumnHeader>
          <Skeleton height='16px' width='72px' />
        </Table.ColumnHeader>
        <Table.ColumnHeader />
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {[0, 1].map((row) => (
        <Table.Row key={row}>
          <Table.Cell>
            <Skeleton height='16px' width='220px' />
          </Table.Cell>
          <Table.Cell>
            <Skeleton height='16px' width='104px' />
          </Table.Cell>
          <Table.Cell>
            <Skeleton height='24px' width='82px' borderRadius='full' />
          </Table.Cell>
          <Table.Cell>
            <Skeleton height='32px' width='32px' borderRadius='md' ml='auto' />
          </Table.Cell>
        </Table.Row>
      ))}
    </Table.Body>
  </Table.Root>
);

const AccessSectionSkeleton = ({ table = 'users' }: { table?: 'users' | 'tokens' }) => (
  <Grid templateColumns={{ base: '1fr', lg: '1fr 2fr' }} gap={{ base: 4, lg: 10 }}>
    <GridItem>
      <Stack gap={2}>
        <Skeleton height='20px' width='120px' />
        <Skeleton height='14px' width='240px' />
        <Skeleton height='14px' width='190px' />
      </Stack>
    </GridItem>
    <GridItem minW={0}>{table === 'users' ? <AccessUsersSkeleton /> : <AuthTokenListSkeleton />}</GridItem>
  </Grid>
);

export const AccessPageSkeleton = () => (
  <Stack gap={12} align='stretch' my={8} aria-busy='true'>
    <AccessSectionSkeleton />
    <AccessSectionSkeleton table='tokens' />
  </Stack>
);
